import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../lib/analytics';
import {
  getStoredLocalAccessToken,
  clearLocalWixTokens,
  clearServerSession,
  fetchServerSession,
  getStoredLocalWixTokens,
  startServerSession,
} from '../lib/sessionAuth';
import { wixClient } from '../config/wix.mjs';

interface AuthContextType {
  userEmail: string | null;
  userName: string | null;
  accessToken: string | null;
  isPremium: boolean;
  authReady: boolean;
  setUserInfo: (email: string | null, name: string | null, token: string | null) => void;
  setIsPremium: (isPremium: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Bootstrap from localStorage for fast first render, then validate with server session.
  const [userEmail, setUserEmail] = useState<string | null>(() => localStorage.getItem('userEmail'));
  const [userName, setUserName] = useState<string | null>(() => localStorage.getItem('userName'));
  const [accessToken, setAccessToken] = useState<string | null>(() => getStoredLocalAccessToken());
  const [isPremium, setIsPremium] = useState(() => localStorage.getItem('isPremium') === 'true');
  const [authReady, setAuthReady] = useState(false);

  const clearLocalState = () => {
    setUserEmail(null);
    setUserName(null);
    setAccessToken(null);
    setIsPremium(false);
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('isPremium');
    clearLocalWixTokens();
  };

  useEffect(() => {
    let active = true;

    const hydrateSession = async () => {
      try {
        let session = await fetchServerSession();
        if (!session?.accessToken) {
          const storedTokens = getStoredLocalWixTokens();
          if (storedTokens) {
            try {
              wixClient.auth.setTokens(storedTokens as any);
            } catch (error) {
              console.warn('Failed to hydrate Wix client tokens from local storage:', error);
            }
            session = await startServerSession(storedTokens);
          }
        }

        if (!active) return;

        if (session?.accessToken) {
          setUserEmail(session.userEmail);
          setUserName(session.userName);
          setAccessToken(session.accessToken);
          setIsPremium(Boolean(session.isPremium));
        } else {
          clearLocalState();
        }
      } catch (error) {
        console.warn('Failed to hydrate auth session from server:', error);
        if (active) {
          clearLocalState();
        }
      } finally {
        if (active) {
          setAuthReady(true);
        }
      }
    };

    void hydrateSession();

    return () => {
      active = false;
    };
  }, []);

  // Update localStorage when auth state changes
  useEffect(() => {
    if (userEmail) {
      localStorage.setItem('userEmail', userEmail);
    } else {
      localStorage.removeItem('userEmail');
    }
  }, [userEmail]);

  useEffect(() => {
    if (userName) {
      localStorage.setItem('userName', userName);
    } else {
      localStorage.removeItem('userName');
    }
  }, [userName]);

  useEffect(() => {
    localStorage.setItem('isPremium', isPremium.toString());
  }, [isPremium]);

  useEffect(() => {
    if (userEmail) {
      identifyAnalyticsUser(userEmail, {
        user_name: userName,
        is_premium: isPremium,
      });
      return;
    }

    resetAnalyticsUser();
  }, [userEmail, userName, isPremium]);

  const setUserInfo = (email: string | null, name: string | null, token: string | null) => {
    setUserEmail(email);
    setUserName(name);
    setAccessToken(token);
  };

  const logout = () => {
    console.log('🚪 Logging out user - clearing all auth data');
    clearLocalState();
    void clearServerSession();
  };

  return (
    <AuthContext.Provider value={{ 
      userEmail, 
      userName, 
      accessToken, 
      isPremium,
      authReady,
      setUserInfo,
      setIsPremium,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
